import { useEffect, useState } from 'react'
import { Loader2, UserCircle, Building2, Network } from 'lucide-react'

export interface CommissionTreeNode {
  id: string
  name: string
  type: string
  role: 'REDE' | 'DIRETO'
  networkPercentage: number | null
}

export interface CommissionInfo {
  manager: { id: string; name: string; type: string } | null
  chain: CommissionTreeNode[]
  company?: { id: string; name: string } | null
}

/** Rótulo amigável do tipo de usuário. */
function typeLabel(t: string): string {
  const map: Record<string, string> = {
    DIRETOR: 'Diretor', SUPERVISOR: 'Supervisor', AFILIADO: 'Afiliado', VENDEDOR: 'Vendedor',
    ADMINISTRADOR: 'Administrador', COLABORADOR: 'Colaborador',
  }
  return map[t] ?? t
}

function roleBadge(node: CommissionTreeNode) {
  if (node.role === 'REDE') {
    const pct = node.networkPercentage != null ? `${(node.networkPercentage * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%` : ''
    return <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-200">Rede {pct}</span>
  }
  return <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-sky-200">Direto</span>
}

/**
 * Painel "Informações" (somente leitura): responsável direto + árvore de
 * comissionamento e, opcionalmente, a empresa. Compartilhado pelos forms de
 * associado e empresa. Carrega sob demanda via `load` quando `enabled`.
 */
export function CommissionInfoPanel({ enabled, load }: {
  enabled: boolean
  load: () => Promise<CommissionInfo>
}) {
  const [data, setData] = useState<CommissionInfo | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return
    setLoading(true)
    load().then(setData).catch(() => setData(null)).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  if (!enabled) {
    return <p className="text-xs text-gray-400">Salve o registro para visualizar as informações de comissionamento.</p>
  }
  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
  }
  if (!data) {
    return <p className="text-xs text-gray-400">Não foi possível carregar as informações.</p>
  }

  // Árvore exibida do topo → responsável (cadeia vem responsável → topo).
  const topDown = [...data.chain].reverse()

  return (
    <div className="space-y-6">
      {/* Empresa (quando houver) */}
      {data.company && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Empresa</h3>
          <div className="inline-flex items-center gap-2 rounded-md ring-1 ring-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
            <Building2 size={15} className="text-gray-400" />
            {data.company.name}
          </div>
        </div>
      )}

      {/* Responsável direto */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Responsável direto</h3>
        {data.manager ? (
          <div className="inline-flex items-center gap-2 rounded-md ring-1 ring-gray-200 bg-white px-3 py-2 text-sm">
            <UserCircle size={16} className="text-brand-500" />
            <span className="font-medium text-gray-800">{data.manager.name}</span>
            <span className="text-xs text-gray-500">· {typeLabel(data.manager.type)}</span>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Sem responsável vinculado.</p>
        )}
      </div>

      {/* Árvore de comissionamento */}
      <div>
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <Network size={13} /> Árvore de comissionamento
        </h3>
        {topDown.length === 0 ? (
          <p className="text-xs text-gray-400">Sem cadeia de comissionamento.</p>
        ) : (
          <ul className="space-y-1.5">
            {topDown.map((node, i) => (
              <li key={node.id} className="flex items-center gap-2" style={{ paddingLeft: `${i * 18}px` }}>
                {i > 0 && <span className="text-gray-300">↳</span>}
                <div className="flex flex-1 items-center justify-between gap-3 rounded-md ring-1 ring-gray-200 bg-white px-3 py-2">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-800">{node.name}</span>
                    <span className="text-xs text-gray-500">· {typeLabel(node.type)}</span>
                  </span>
                  {roleBadge(node)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
